'use client'

import { useState } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { useLang } from '../context/LangContext'
import { t, productDevOps } from '../lib/translations'
import { devopsCategories } from '../data/devopsTools'

export default function DevOpsToolsTabs({ className = '' }: { className?: string }) {
  const lang = useLang()
  const [activeTab, setActiveTab] = useState(devopsCategories[0].id)
  const validActiveTab = devopsCategories.some(c => c.id === activeTab) ? activeTab : devopsCategories[0].id

  return (
    <div className={`w-full ${className}`}>
      {/* Tabs */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
        {devopsCategories.map((category) => {
          const isActive = validActiveTab === category.id
          return (
            <button
              key={category.id}
              onClick={() => setActiveTab(category.id)}
              className={`relative px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                lang === 'fa' ? 'font-farsi' : lang === 'ar' ? 'font-arabic' : 'font-sans'
              } ${
                isActive
                  ? 'text-white'
                  : 'text-[#94a3b8] hover:text-white hover:bg-white/[0.05]'
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeTabDevOps"
                  className="absolute inset-0 bg-gradient-to-r from-[#4f46e5]/80 to-[#7c3aed]/80 rounded-full shadow-lg"
                  initial={false}
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10">
                {t(lang, productDevOps.categories[category.translationKey as keyof typeof productDevOps.categories])}
              </span>
            </button>
          )
        })}
      </div>

      {/* Content */}
      <div className="relative min-h-[200px]">
        <AnimatePresence mode="wait">
          {devopsCategories.map((category) => {
            if (validActiveTab !== category.id) return null
            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
              >
                {category.tools.map((tool) => (
                  <div
                    key={tool.name}
                    className="dir-ltr flex flex-col items-center justify-center gap-4 rounded-xl border border-white/[0.05] bg-white/[0.02] py-6 hover:bg-white/[0.05] hover:border-white/[0.1] transition-all duration-300"
                  >
                    <div className="w-12 h-12 relative flex items-center justify-center transition-all duration-300">
                      <Image
                        src={tool.logo}
                        alt={tool.name}
                        width={48}
                        height={48}
                        className="object-contain drop-shadow-md"
                      />
                    </div>
                    <span className="text-sm font-medium text-[#cbd5e1]">{tool.name}</span>
                  </div>
                ))}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}
